/**
 * AI images, the paid add-on (specs/agents CONTRACT-v1.4 §3, §5.4, §9). Shapes read from the AI API's
 * staff routes `GET api/admin/images/usage` and `GET api/admin/images/generations`; the second passes the
 * account API's `GET api/M2M/images/generations` list through. Property names are the API's camelCase JSON.
 * Costs are what Plus pays the provider, in USD — platform staff only, never shown to owners (A20).
 */

/** `agent.ImageGenerations.Status` (CONTRACT-v1.4 §1). */
export type ImageGenerationStatus = 'Reserved' | 'Completed' | 'Released' | 'Refused' | 'Failed';

export const IMAGE_GENERATION_STATUSES: ReadonlyArray<ImageGenerationStatus> = ['Reserved', 'Completed', 'Released', 'Refused', 'Failed'];

/** Where the request came from: the website builder's image picker, or a website agent run. */
export type ImagePurpose = 'builder' | 'agent-run';

/**
 * Refusal codes (CONTRACT-v1.4 §6.3): five checked on the description before anything is reserved, and
 * `provider` for a provider safety refusal after the reservation (released, so the owner loses nothing).
 */
export const IMAGE_REFUSAL_CODES: ReadonlyArray<string> = ['people', 'brand', 'text', 'product', 'instructions', 'provider'];

/** The add-on's feature (CONTRACT-v1.4 §1 `dbo.Features`); a plan grants the add-on when it includes this. */
export const AI_IMAGES_FEATURE = 'AI Images';

/** The registry key the image model is assigned under (CONTRACT-v1.4 §3, AI › Assignments). */
export const IMAGE_GENERATION_FEATURE_KEY = 'agent.image-generation';

/** Image quality sent to the provider on every request (platform config, never an owner choice — plan D44). */
export type ImageQuality = 'low' | 'medium' | 'high';

export const IMAGE_QUALITIES: ReadonlyArray<ImageQuality> = ['low', 'medium', 'high'];

/** The most images one website agent run may create (CONTRACT-v1.4 §5.5 `options.images.max` 1..8). */
export const AGENT_RUN_IMAGES_LIMIT = 8;

/**
 * The editable platform settings for AI images (owner decision 2026-10-28: configured in Plus.Admin, not in an
 * app setting): the account API's `PUT api/admin/agents/images/settings` body, stored in the one-row
 * `agent.ImageSettings`. `enabled` is the platform switch: off, every new request is refused for every business.
 */
export interface ImageSettingsUpdate {
  enabled: boolean;
  quality: ImageQuality;
  /** Words a description may not name (the `brand` refusal, §6.3), on top of "logo", "trademark" and "brand". */
  brandTerms: string[];
  /** Images a website agent run creates when the owner opts in without changing the number. */
  agentRunDefaultMax: number;
  /** The most an owner may pick for one run; 1 ≤ default ≤ cap ≤ 8. */
  agentRunMaxCap: number;
}

/** `GET api/admin/agents/images/settings`: the stored settings and who last changed them. */
export interface ImageSettings extends ImageSettingsUpdate {
  updatedAt: string | null;
  updatedBy: string | null;
}

/** One stored candidate of a generation: the media library item Retail.API wrote (CONTRACT-v1.4 §4). */
export interface ImageThumbnail {
  mediaId: string;
  url: string;
  width?: number | null;
  height?: number | null;
}

/** One `agent.ImageGenerations` row, newest first (CONTRACT-v1.4 §3 `ImageGenerationRow`). */
export interface ImageGenerationRow {
  generationId: string;
  accountUserId: string;
  businessId: string;
  executionId: string | null;
  /** The user who asked (the owner or staff member in the builder, the agent run's initiator). */
  requestedBy: string;
  purpose: ImagePurpose | string;
  siteId: string | null;
  /** e.g. `pages[0].sections[1].settings.mediaId`. */
  slotKey: string | null;
  /** 1–3 images asked for. */
  requested: number;
  /** Images the provider returned (counted against the allowance once Completed). */
  returned: number;
  status: ImageGenerationStatus | string;
  refusalCode: string | null;
  /** What Plus paid the provider for this request, USD. A provider refusal can still cost money. */
  costUsd: number;
  providerModel: string | null;
  descriptionHash: string | null;
  /** First day of the UTC month the request counts in (yyyy-MM-dd). */
  monthUtc: string;
  reservedAt: string;
  completedAt: string | null;
  /** Usage route only (§5.4 "generations with thumbnails"): the candidates the request stored. */
  thumbnails?: ImageThumbnail[] | null;
}

/**
 * `GET api/admin/images/usage?businessId&from&to` (CONTRACT-v1.4 §5.4): the business's allowance this
 * month — the account-wide figure of `GET api/M2M/images/{businessId}/allowance` (§3) — the provider cost
 * of the range and its generations with thumbnails.
 */
export interface BusinessImageUsage {
  businessId?: string;
  /** Σ of the account's live AI images add-ons' monthly allowances; 0 without one. */
  allowance: number;
  used: number;
  reserved: number;
  left: number;
  monthUtc?: string | null;
  resetsAtUtc?: string | null;
  /** Provider cost over the range, USD. Absent → the console sums the generations' `costUsd`. */
  costUsd?: number | null;
  generations?: ImageGenerationRow[] | null;
}

/** Filters of `GET api/admin/images/generations` (§3: `businessId?, accountUserId?, status?, from?, to?, take (1–500)`). */
export interface ImageGenerationQuery {
  businessId?: string | null;
  accountUserId?: string | null;
  status?: ImageGenerationStatus | string | null;
  /** ISO instants. */
  from?: string | null;
  to?: string | null;
  take?: number | null;
}

/** The platform page's month row, aggregated in the console from the generation list. */
export interface ImageMonthSummary {
  /** yyyy-MM-01 */
  monthUtc: string;
  /** Generation rows (requests that reserved). */
  requests: number;
  /** Images created: Σ `returned` of Completed rows — what counted against allowances. */
  images: number;
  costUsd: number;
  /** Refused rows by refusal code (`unknown` when a row carries none). */
  refusals: Record<string, number>;
  refused: number;
  failed: number;
  released: number;
  /** Distinct businesses that made a request in the month. */
  businesses: number;
}
